import React, { useState, useEffect } from 'react';
import { 
  Row, 
  Col, 
  Card, 
  Form, 
  Button, 
  InputGroup,
  Badge,
  Pagination,
  Dropdown,
  DropdownButton,
  Alert,
  Spinner,
  Collapse,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropertyDetailsModal from '../components/PropertyDetailsModal';
import PropertyCardEnhanced from '../components/PropertyCardEnhanced';
import '../style.css';
import { 
  faSearch,
  faFilter,
  faMapMarkerAlt,
  faDollarSign,
  faBed,
  faBath,
  faRuler,
  faHeart,
  faEye,
  faSort,
  faHome,
  faExclamationTriangle,
  faChevronDown,
  faChevronUp,
  faSwimmingPool,
  faCar,
  faCalendarAlt,
  faCoins,
  faExpand
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { useAtom } from 'jotai';
import { user_info } from 'jotai/UserJotaiInstance';

interface PropertySearchProps {
  isAgent: boolean;
}

interface SearchFilters {
  location: string;
  city: string;
  state: string;
  zipCode: string;
  minPrice: number;
  maxPrice: number;
  bedrooms: number;
  bathrooms: number;
  propertyType: string;
  sqftMin: number;
  sqftMax: number;
  hasPool: boolean;
  minGarage: number;
  yearBuiltMin: number;
  daysOnMarket: number;
  maxHOA: number | undefined;
}

interface Property {
  id: string;
  mlsNumber: string;
  image?: string;
  images?: string[];
  price: number;
  bedrooms: number;
  bathrooms: number;
  squareFeet: number;
  sqft?: number; // Alternative field name
  address: string | { street?: string; city?: string; state?: string; zip?: string };
  city: string;
  state: string;
  zipCode: string;
  zip?: string; // Alternative field name
  daysOnMarket?: number;
  propertyType: string;
  features?: string[];
  description?: string;
  listingDate?: string;
  status?: string;
  yearBuilt?: number;
  lotSize?: number;
  garage?: number;
  pool?: boolean;
  latitude?: number;
  longitude?: number;
  hoaFee?: number;
  propertySubType?: string;
  listingAgent?: {
    name?: string;
    phone?: string;
    email?: string;
    officeName?: string;
  };
}

const PropertySearchEnhanced: React.FC<PropertySearchProps> = ({ isAgent }) => {
  const [userInfo] = useAtom(user_info);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [savedProperties, setSavedProperties] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<SearchFilters>({
    location: '',
    city: 'Phoenix',
    state: 'AZ',
    zipCode: '',
    minPrice: 0,
    maxPrice: 0,
    bedrooms: 0,
    bathrooms: 0,
    propertyType: 'all',
    sqftMin: 0,
    sqftMax: 0,
    hasPool: false,
    minGarage: 0,
    yearBuiltMin: 0,
    daysOnMarket: 0,
    maxHOA: undefined
  });

  const [searchResults, setSearchResults] = useState<Property[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMLSAccess, setHasMLSAccess] = useState<boolean | null>(null);
  const [sortBy, setSortBy] = useState('price-low');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [resultsPerPage, setResultsPerPage] = useState(24); // User can control this

  // Popular Arizona cities for quick selection (cities with available data first)
  const popularCities = [
    'Phoenix', 'Scottsdale', 'Tempe', 'Mesa', 'Chandler', 
    'Gilbert', 'Glendale', 'Peoria', 'Surprise', 'Avondale'
  ];

  // Cities that currently have data available
  const citiesWithData = ['Phoenix', 'Scottsdale', 'Tempe'];

  // Check if user has MLS access on component mount
  useEffect(() => {
    checkMLSAccess();
  }, [userInfo]);

  // Initial load of properties
  useEffect(() => {
    if (hasMLSAccess !== null) {
      handleSearch();
    }
  }, [hasMLSAccess]);

  const checkMLSAccess = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setHasMLSAccess(false);
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL?.replace(/\/+$/, '') || 'http://localhost:8080'}/api/mls-portal/credentials/status`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setHasMLSAccess(response.data.hasCredentials);
    } catch (err) {
      console.error('Error checking MLS access:', err);
      setHasMLSAccess(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };

  const handleSearch = async () => {
    console.log('🔍 [DEBUG] handleSearch triggered with filters:', filters);
    setIsLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('authToken');
      
      // Always use live search endpoint if available, fallback to test endpoint
      let endpoint = '/mls-portal/properties/live/test';  // Use test endpoint that doesn't require auth
      let params: any = {
        limit: resultsPerPage,
        page: currentPage
      };

      // Add location filters
      if (filters.location) {
        // Handle "City, State" format
        const [city, state] = filters.location.split(',').map(s => s.trim());
        if (city) params.city = city;
        if (state) params.state = state;
      } else {
        if (filters.city) params.city = filters.city;
        if (filters.state) params.state = filters.state;
      }
      if (filters.zipCode) params.zipCode = filters.zipCode;

      // Price range
      if (filters.minPrice > 0) params.minPrice = filters.minPrice;
      if (filters.maxPrice > 0) params.maxPrice = filters.maxPrice;

      // Property details
      if (filters.bedrooms > 0) params.bedrooms = filters.bedrooms;
      if (filters.bathrooms > 0) params.bathrooms = filters.bathrooms;
      if (filters.propertyType !== 'all') params.propertyType = filters.propertyType;

      // Square footage
      if (filters.sqftMin > 0) params.sqftMin = filters.sqftMin;
      if (filters.sqftMax > 0) params.sqftMax = filters.sqftMax;

      // Advanced filters
      if (showAdvancedFilters) {
        if (filters.hasPool) params.hasPool = true;
        if (filters.minGarage > 0) params.minGarage = filters.minGarage;
        if (filters.yearBuiltMin > 0) params.yearBuiltMin = filters.yearBuiltMin;
        if (filters.daysOnMarket > 0) params.daysOnMarket = filters.daysOnMarket;
        if (filters.maxHOA !== undefined) params.maxHOA = filters.maxHOA;
      }

      // Sorting
      switch (sortBy) {
        case 'price-low':
          params.orderBy = 'ListPrice';
          break;
        case 'price-high':
          params.orderBy = '-ListPrice';
          break;
        case 'newest':
          params.orderBy = '-ListingContractDate';
          break;
        case 'sqft':
          params.orderBy = '-LivingArea';
          break;
        case 'beds':
          params.orderBy = '-BedroomsTotal';
          break;
      }

      // For now, stick with test endpoint that works without auth
      // if (token && hasMLSAccess) {
      //   endpoint = '/mls-portal/properties/live/search';
      // }

      console.log('🔍 Searching with params:', params);

      const fullUrl = `${process.env.REACT_APP_API_URL?.replace(/\/+$/, '') || 'http://localhost:8080/api'}${endpoint}`;
      console.log('🌐 [DEBUG] Making API request to:', fullUrl, 'with params:', params);
      
      const response = await axios.get(fullUrl, {
        params,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.data.success) {
        // Handle different data structures from backend
        const properties = response.data.data || response.data.properties || [];
        console.log('✅ [DEBUG] API response successful. Properties received:', properties.length);
        console.log('📊 [DEBUG] First property sample:', properties[0]);
        
        setSearchResults(properties);
        setTotalResults(
          response.data.pagination?.total || 
          response.data.totalFound || 
          response.data.total || 
          properties.length
        );
        
        // Show source of data
        if (response.data.source === 'armls') {
          console.log('📡 Live ARMLS data loaded');
        } else if (response.data.source === 'cache') {
          console.log('💾 Cached data loaded (ARMLS temporarily unavailable)');
        }
      } else {
        throw new Error('Failed to fetch properties');
      }
    } catch (err: any) {
      console.error('Search error:', err);
      
      // Fallback to test endpoint for development
      if (process.env.NODE_ENV === 'development') {
        try {
          const testParams: any = { 
            city: filters.city || 'Phoenix',
            limit: resultsPerPage
          };
          
          // Pass all filter params to test endpoint too
          if (filters.minPrice > 0) testParams.minPrice = filters.minPrice;
          if (filters.maxPrice > 0) testParams.maxPrice = filters.maxPrice;
          if (filters.bedrooms > 0) testParams.bedrooms = filters.bedrooms;
          
          const testResponse = await axios.get(
            `${process.env.REACT_APP_API_URL?.replace(/\/+$/, '') || 'http://localhost:8080'}/api/mls-portal/properties/live/test`,
            { params: testParams }
          );
          
          if (testResponse.data.success) {
            setSearchResults(testResponse.data.data || []);
            setTotalResults(testResponse.data.totalFound || 0);
            setError('Using test data - Live connection requires authentication');
          }
        } catch (testErr) {
          setError('Unable to fetch properties. Please try again later.');
        }
      } else {
        setError(err.response?.data?.message || 'Failed to search properties');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-search on filter changes (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (filters.city || filters.location) {
        handleSearch();
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [filters, currentPage, sortBy, hasMLSAccess, resultsPerPage]);

  const handleFilterChange = (key: keyof SearchFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1); // Reset to first page on filter change
  };

  const resetFilters = () => {
    setFilters({
      location: '',
      city: 'Phoenix',
      state: 'AZ',
      zipCode: '',
      minPrice: 0,
      maxPrice: 0,
      bedrooms: 0,
      bathrooms: 0,
      propertyType: 'all',
      sqftMin: 0,
      sqftMax: 0,
      hasPool: false,
      minGarage: 0,
      yearBuiltMin: 0,
      daysOnMarket: 0,
      maxHOA: undefined
    });
  };

  const handleViewPropertyDetails = (property: any) => {
    setSelectedProperty(property);
    setShowDetailsModal(true);
  };

  const handleSaveToggle = async (property: any) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setError('Please login to save properties');
      return;
    }

    const mlsNumber = property.mlsNumber || property.mls_number;
    const isSaved = savedProperties.has(mlsNumber);

    try {
      const endpoint = isSaved 
        ? `/api/mls-portal/actions/save/${mlsNumber}`
        : '/api/mls-portal/actions/save';
      
      const method = isSaved ? 'DELETE' : 'POST';
      
      await axios({
        method,
        url: `${process.env.REACT_APP_API_URL}${endpoint}`,
        data: !isSaved ? {
          mlsNumber: mlsNumber,
          propertyData: property
        } : undefined,
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // Update saved properties set
      const newSaved = new Set(savedProperties);
      if (isSaved) {
        newSaved.delete(mlsNumber);
      } else {
        newSaved.add(mlsNumber);
      }
      setSavedProperties(newSaved);
    } catch (error) {
      console.error('Error toggling save:', error);
      setError('Failed to update saved property');
    }
  };

  // Check saved properties when results change
  useEffect(() => {
    const checkSavedProperties = async () => {
      const token = localStorage.getItem('authToken');
      if (!token || searchResults.length === 0) return;

      try {
        const mlsNumbers = searchResults.map(p => p.mlsNumber || (p as any).mls_number).filter(Boolean);
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL?.replace(/\/+$/, '') || 'http://localhost:8080'}/api/mls-portal/actions/check-saved`,
          { mlsNumbers },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        const savedMap = response.data.data;
        const newSaved = new Set<string>();
        Object.entries(savedMap).forEach(([mlsNumber, isSaved]) => {
          if (isSaved) newSaved.add(mlsNumber);
        });
        setSavedProperties(newSaved);
      } catch (error) {
        console.error('Error checking saved properties:', error);
      }
    };

    checkSavedProperties();
  }, [searchResults]);

  return (
    <div className="property-search-enhanced">
      {/* Quick City Selection */}
      <div className="mb-3">
        <div className="d-flex flex-wrap gap-2">
          <small className="text-muted align-self-center">Popular Cities:</small>
          {popularCities.map(city => {
            const hasData = citiesWithData.includes(city);
            return (
              <Badge 
                key={city}
                bg={filters.city === city ? 'primary' : (hasData ? 'success' : 'light')}
                text={filters.city === city ? 'white' : (hasData ? 'white' : 'dark')}
                className="cursor-pointer"
                onClick={() => handleFilterChange('city', city)}
                style={{ fontSize: '0.875rem', padding: '0.5em 0.75em' }}
                title={hasData ? `${city} - Properties available` : `${city} - No properties currently`}
              >
                {city} {hasData && '✓'}
              </Badge>
            );
          })}
        </div>
      </div>

      {/* Main Search Filters */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="g-3">
            {/* Location Search */}
            <Col lg={3}>
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="City, State or ZIP"
                  value={filters.location || filters.city}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.includes(',')) {
                      handleFilterChange('location', value);
                    } else if (/^\d+$/.test(value)) {
                      handleFilterChange('zipCode', value);
                    } else {
                      handleFilterChange('city', value);
                    }
                  }}
                />
              </InputGroup>
            </Col>

            {/* Price Range */}
            <Col lg={3}>
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faDollarSign} />
                </InputGroup.Text>
                <Form.Select
                  value={filters.minPrice}
                  onChange={(e) => handleFilterChange('minPrice', Number(e.target.value))}
                  size="sm"
                >
                  <option value={0}>No Min</option>
                  <option value={50000}>$50k</option>
                  <option value={75000}>$75k</option>
                  <option value={100000}>$100k</option>
                  <option value={125000}>$125k</option>
                  <option value={150000}>$150k</option>
                  <option value={175000}>$175k</option>
                  <option value={200000}>$200k</option>
                  <option value={225000}>$225k</option>
                  <option value={250000}>$250k</option>
                  <option value={275000}>$275k</option>
                  <option value={300000}>$300k</option>
                  <option value={325000}>$325k</option>
                  <option value={350000}>$350k</option>
                  <option value={375000}>$375k</option>
                  <option value={400000}>$400k</option>
                  <option value={450000}>$450k</option>
                  <option value={500000}>$500k</option>
                  <option value={600000}>$600k</option>
                  <option value={700000}>$700k</option>
                  <option value={800000}>$800k</option>
                  <option value={900000}>$900k</option>
                  <option value={1000000}>$1M</option>
                  <option value={1500000}>$1.5M</option>
                  <option value={2000000}>$2M</option>
                  <option value={3000000}>$3M</option>
                  <option value={5000000}>$5M</option>
                </Form.Select>
                <span className="input-group-text px-2">to</span>
                <Form.Select
                  value={filters.maxPrice}
                  onChange={(e) => handleFilterChange('maxPrice', Number(e.target.value))}
                  size="sm"
                >
                  <option value={0}>No Max</option>
                  <option value={75000}>$75k</option>
                  <option value={100000}>$100k</option>
                  <option value={125000}>$125k</option>
                  <option value={150000}>$150k</option>
                  <option value={175000}>$175k</option>
                  <option value={200000}>$200k</option>
                  <option value={225000}>$225k</option>
                  <option value={250000}>$250k</option>
                  <option value={275000}>$275k</option>
                  <option value={300000}>$300k</option>
                  <option value={325000}>$325k</option>
                  <option value={350000}>$350k</option>
                  <option value={375000}>$375k</option>
                  <option value={400000}>$400k</option>
                  <option value={450000}>$450k</option>
                  <option value={500000}>$500k</option>
                  <option value={600000}>$600k</option>
                  <option value={700000}>$700k</option>
                  <option value={800000}>$800k</option>
                  <option value={900000}>$900k</option>
                  <option value={1000000}>$1M</option>
                  <option value={1500000}>$1.5M</option>
                  <option value={2000000}>$2M</option>
                  <option value={3000000}>$3M</option>
                  <option value={5000000}>$5M</option>
                  <option value={10000000}>$10M+</option>
                </Form.Select>
              </InputGroup>
            </Col>

            {/* Beds/Baths */}
            <Col lg={2}>
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faBed} />
                </InputGroup.Text>
                <Form.Select
                  value={filters.bedrooms}
                  onChange={(e) => handleFilterChange('bedrooms', Number(e.target.value))}
                  size="sm"
                >
                  <option value={0}>Any Beds</option>
                  <option value={1}>1+</option>
                  <option value={2}>2+</option>
                  <option value={3}>3+</option>
                  <option value={4}>4+</option>
                  <option value={5}>5+</option>
                </Form.Select>
              </InputGroup>
            </Col>

            <Col lg={2}>
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faBath} />
                </InputGroup.Text>
                <Form.Select
                  value={filters.bathrooms}
                  onChange={(e) => handleFilterChange('bathrooms', Number(e.target.value))}
                  size="sm"
                >
                  <option value={0}>Any Baths</option>
                  <option value={1}>1+</option>
                  <option value={1.5}>1.5+</option>
                  <option value={2}>2+</option>
                  <option value={2.5}>2.5+</option>
                  <option value={3}>3+</option>
                  <option value={4}>4+</option>
                </Form.Select>
              </InputGroup>
            </Col>

            {/* Property Type */}
            <Col lg={2}>
              <Form.Select
                value={filters.propertyType}
                onChange={(e) => handleFilterChange('propertyType', e.target.value)}
                size="sm"
              >
                <option value="all">All Types</option>
                <option value="single_family">Single Family</option>
                <option value="condo">Condo</option>
                <option value="townhouse">Townhouse</option>
                <option value="multi_family">Multi-Family</option>
                <option value="land">Land</option>
                <option value="commercial">Commercial</option>
              </Form.Select>
            </Col>
          </Row>

          {/* Square Footage Row */}
          <Row className="g-3 mt-1">
            <Col lg={4}>
              <InputGroup size="sm">
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faExpand} />
                </InputGroup.Text>
                <Form.Control
                  type="number"
                  placeholder="Min Sq Ft"
                  value={filters.sqftMin || ''}
                  onChange={(e) => handleFilterChange('sqftMin', Number(e.target.value))}
                />
                <span className="input-group-text">to</span>
                <Form.Control
                  type="number"
                  placeholder="Max Sq Ft"
                  value={filters.sqftMax || ''}
                  onChange={(e) => handleFilterChange('sqftMax', Number(e.target.value))}
                />
              </InputGroup>
            </Col>

            {/* Advanced Filters Toggle */}
            <Col lg={8} className="text-end">
              <Button
                variant="link"
                size="sm"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="text-decoration-none"
              >
                <FontAwesomeIcon 
                  icon={showAdvancedFilters ? faChevronUp : faChevronDown} 
                  className="me-1" 
                />
                {showAdvancedFilters ? 'Hide' : 'Show'} Advanced Filters
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={resetFilters}
                className="ms-2"
              >
                Reset Filters
              </Button>
              <Button 
                variant="primary" 
                size="sm"
                onClick={handleSearch}
                disabled={isLoading}
                className="ms-2"
              >
                {isLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSearch} className="me-1" />
                    Search
                  </>
                )}
              </Button>
            </Col>
          </Row>

          {/* Advanced Filters */}
          <Collapse in={showAdvancedFilters}>
            <div>
              <hr className="my-3" />
              <Row className="g-3">
                <Col md={3}>
                  <Form.Check
                    type="checkbox"
                    id="hasPool"
                    label={
                      <>
                        <FontAwesomeIcon icon={faSwimmingPool} className="me-1" />
                        Must Have Pool
                      </>
                    }
                    checked={filters.hasPool}
                    onChange={(e) => handleFilterChange('hasPool', e.target.checked)}
                  />
                </Col>

                <Col md={3}>
                  <InputGroup size="sm">
                    <InputGroup.Text>
                      <FontAwesomeIcon icon={faCar} />
                    </InputGroup.Text>
                    <Form.Select
                      value={filters.minGarage}
                      onChange={(e) => handleFilterChange('minGarage', Number(e.target.value))}
                    >
                      <option value={0}>Any Garage</option>
                      <option value={1}>1+ Car</option>
                      <option value={2}>2+ Car</option>
                      <option value={3}>3+ Car</option>
                      <option value={4}>4+ Car</option>
                    </Form.Select>
                  </InputGroup>
                </Col>

                <Col md={3}>
                  <InputGroup size="sm">
                    <InputGroup.Text>Built After</InputGroup.Text>
                    <Form.Control
                      type="number"
                      placeholder="Year"
                      value={filters.yearBuiltMin || ''}
                      onChange={(e) => handleFilterChange('yearBuiltMin', Number(e.target.value))}
                      min="1900"
                      max={new Date().getFullYear()}
                    />
                  </InputGroup>
                </Col>

                <Col md={3}>
                  <InputGroup size="sm">
                    <InputGroup.Text>Max HOA</InputGroup.Text>
                    <Form.Control
                      type="number"
                      placeholder="$/month"
                      value={filters.maxHOA ?? ''}
                      onChange={(e) => handleFilterChange('maxHOA', e.target.value ? Number(e.target.value) : undefined)}
                    />
                  </InputGroup>
                </Col>

                <Col md={3}>
                  <InputGroup size="sm">
                    <InputGroup.Text>On Market</InputGroup.Text>
                    <Form.Select
                      value={filters.daysOnMarket}
                      onChange={(e) => handleFilterChange('daysOnMarket', Number(e.target.value))}
                    >
                      <option value={0}>Any Time</option>
                      <option value={1}>Today</option>
                      <option value={7}>Last 7 Days</option>
                      <option value={14}>Last 14 Days</option>
                      <option value={30}>Last 30 Days</option>
                      <option value={90}>Last 90 Days</option>
                    </Form.Select>
                  </InputGroup>
                </Col>
              </Row>
            </div>
          </Collapse>
        </Card.Body>
      </Card>

      {/* Search Results */}
      {error && (
        <Alert variant="warning" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" className="mb-3" />
          <p className="text-muted">Searching properties...</p>
        </div>
      ) : (
        <>
          {/* Results Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h5 className="mb-0">
                {totalResults > 0 ? `${totalResults.toLocaleString()} Properties Found` : 'No Properties Found'}
              </h5>
              {searchResults.length > 0 && (
                <small className="text-muted">
                  Showing {((currentPage - 1) * resultsPerPage) + 1}-
                  {Math.min(currentPage * resultsPerPage, totalResults)} of {totalResults.toLocaleString()}
                  {filters.city && ` in ${filters.city}`}
                </small>
              )}
            </div>
            
            <div className="d-flex gap-2 align-items-center">
              <DropdownButton
                size="sm"
                variant="outline-secondary"
                title={`Show: ${resultsPerPage}`}
                onSelect={(e) => {
                  setResultsPerPage(Number(e));
                  setCurrentPage(1); // Reset to first page
                }}
              >
                <Dropdown.Item eventKey="12">12 per page</Dropdown.Item>
                <Dropdown.Item eventKey="24">24 per page</Dropdown.Item>
                <Dropdown.Item eventKey="36">36 per page</Dropdown.Item>
                <Dropdown.Item eventKey="48">48 per page</Dropdown.Item>
                <Dropdown.Item eventKey="60">60 per page</Dropdown.Item>
                <Dropdown.Item eventKey="100">100 per page</Dropdown.Item>
              </DropdownButton>

              <DropdownButton
                size="sm"
                variant="outline-secondary"
                title={
                  sortBy === 'price-low' ? 'Price: Low to High' :
                  sortBy === 'price-high' ? 'Price: High to Low' :
                  sortBy === 'newest' ? 'Newest First' :
                  sortBy === 'sqft' ? 'Largest First' :
                  sortBy === 'beds' ? 'Most Bedrooms' : 'Sort'
                }
                onSelect={(e) => setSortBy(e || 'price-low')}
              >
                <Dropdown.Item eventKey="price-low">Price: Low to High</Dropdown.Item>
                <Dropdown.Item eventKey="price-high">Price: High to Low</Dropdown.Item>
                <Dropdown.Item eventKey="newest">Newest First</Dropdown.Item>
                <Dropdown.Item eventKey="beds">Most Bedrooms</Dropdown.Item>
                <Dropdown.Item eventKey="sqft">Largest First</Dropdown.Item>
              </DropdownButton>

              {hasMLSAccess && (
                <Badge bg="success" className="ms-2">
                  <FontAwesomeIcon icon={faHome} className="me-1" />
                  Live MLS Data
                </Badge>
              )}
              {!hasMLSAccess && userInfo && (
                <OverlayTrigger
                  placement="left"
                  overlay={<Tooltip>Add your MLS credentials to access real-time data</Tooltip>}
                >
                  <Badge bg="warning" className="ms-2 cursor-pointer">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                    Limited Data
                  </Badge>
                </OverlayTrigger>
              )}
            </div>
          </div>

          {/* Property Grid */}
          <Row className="g-4">
            {searchResults.map((property) => {
              const mlsNumber = property.mlsNumber || (property as any).mls_number || property.id;
              return (
                <Col key={property.id} lg={4} md={6}>
                  <PropertyCardEnhanced 
                    property={property} 
                    onViewDetails={handleViewPropertyDetails}
                    isSaved={savedProperties.has(mlsNumber)}
                    onSaveToggle={handleSaveToggle}
                  />
                </Col>
              );
            })}
          </Row>

          {/* No Results */}
          {searchResults.length === 0 && !isLoading && (
            <div className="text-center py-5">
              <FontAwesomeIcon icon={faHome} size="3x" className="text-muted mb-3" />
              <h5>No properties found</h5>
              <p className="text-muted">
                Try adjusting your search filters or search in a different area
              </p>
              <Button 
                variant="outline-primary" 
                onClick={resetFilters}
                className="mt-2"
              >
                Reset All Filters
              </Button>
            </div>
          )}

          {/* Pagination */}
          {totalResults > resultsPerPage && (
            <div className="d-flex justify-content-center mt-4">
              <Pagination>
                <Pagination.First 
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                />
                <Pagination.Prev 
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                />
                
                {[...Array(Math.min(10, Math.ceil(totalResults / resultsPerPage)))].map((_, idx) => {
                  const pageNum = idx + 1;
                  const totalPages = Math.ceil(totalResults / resultsPerPage);
                  
                  let displayPageNum = pageNum;
                  if (totalPages > 10) {
                    if (currentPage <= 5) {
                      if (pageNum > 7 && pageNum < totalPages) return null;
                    } else if (currentPage >= totalPages - 4) {
                      displayPageNum = totalPages - 10 + pageNum;
                      if (pageNum < 4) return null;
                    } else {
                      displayPageNum = currentPage - 5 + pageNum;
                      if (pageNum === 1) displayPageNum = 1;
                      if (pageNum === 10) displayPageNum = totalPages;
                      if (pageNum > 1 && pageNum < 10 && (displayPageNum < currentPage - 3 || displayPageNum > currentPage + 3)) return null;
                    }
                  }
                  
                  return (
                    <Pagination.Item
                      key={pageNum}
                      active={displayPageNum === currentPage}
                      onClick={() => setCurrentPage(displayPageNum)}
                    >
                      {displayPageNum}
                    </Pagination.Item>
                  );
                })}
                
                <Pagination.Next 
                  onClick={() => setCurrentPage(prev => 
                    Math.min(Math.ceil(totalResults / resultsPerPage), prev + 1)
                  )}
                  disabled={currentPage === Math.ceil(totalResults / resultsPerPage)}
                />
                <Pagination.Last 
                  onClick={() => setCurrentPage(Math.ceil(totalResults / resultsPerPage))}
                  disabled={currentPage === Math.ceil(totalResults / resultsPerPage)}
                />
              </Pagination>
            </div>
          )}
        </>
      )}
      
      {/* Property Details Modal */}
      <PropertyDetailsModal
        property={selectedProperty}
        show={showDetailsModal}
        onHide={() => {
          setShowDetailsModal(false);
          setSelectedProperty(null);
        }}
      />
    </div>
  );
};

export default PropertySearchEnhanced;